<template>
  <div class="container">
    <div class="checkbox-wrapper">
      <label :for="`${id}-checkbox-${advancedOption}`">{{ label }}</label>

      <input
        type="checkbox"
        :id="`${id}-checkbox-${advancedOption}`"
        :checked="!!value"
        @change="handleOptionToggle"
      />
    </div>

    <div v-if="!!value" class="option-form">
      <div class="input-group">
        <label :for="`${id}-${advancedOption}-code`">{{ parseTranslation(CODE) }}</label>

        <input
          type="text"
          name="code"
          :id="`${id}-${advancedOption}-code`"
          :value="value.code"
          @change="handleConfigChange"
        />
      </div>

      <div class="input-group">
        <label :for="`${id}-${advancedOption}-label`">{{
          parseTranslation(LABEL)
        }}</label>

        <input
          type="text"
          name="label"
          :id="`${id}-${advancedOption}-label`"
          :value="value.label"
          @change="handleConfigChange"
        />
      </div>

      <div class="input-group">
        <label :for="`${id}-${advancedOption}-renderOn`">{{
          parseTranslation(RENDER_ON)
        }}</label>

        <select
          name="renderOn"
          :id="`${id}-${advancedOption}-renderOn`"
          :value="value.renderOn"
          @change="handleConfigChange"
        >
          <option
            v-for="renderOption in renderOptions"
            :key="renderOption.value"
            :value="renderOption.value"
            >{{ parseTranslation(renderOption.label) }}</option
          >
        </select>
      </div>

      <div class="input-group">
        <label :for="`${id}-${advancedOption}-logic`">{{
          parseTranslation(LOGIC_LABEL)
        }}</label>

        <select
          name="logic"
          :id="`${id}-${advancedOption}-logic`"
          :value="value.logic"
          @change="handleConfigChange"
        >
          <option
            v-for="logicOption in logicOptions"
            :key="logicOption.value"
            :value="logicOption.value"
            >{{ logicOption.label }}</option
          >
        </select>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import { advancedOptionToRenderConfig } from '@/utils/advancedOptions'
import { LOGIC_OPTIONS } from '@/utils/constants'
import { getFirstItem } from '@/utils/array'

import { eachAnswerTypes, columnReferenceTypes } from './contants'

import * as translationInput from './translations'
const { default: translations, ...translationKeys } = translationInput

const renderOptions = [
  { value: 'question_wide', label: translationKeys.QUESTION_WIDE },
  {
    value: 'each_alternative',
    label: translationKeys.EACH_ALTERNATIVE,
    types: eachAnswerTypes,
  },
  {
    value: 'as_column',
    label: translationKeys.AS_COLUMN,
    types: columnReferenceTypes,
  },
]

export default {
  name: 'AdvancedInheritance',

  props: {
    id: {
      type: String,
      required: true,
    },
    question_index: {
      required: true,
      type: Number,
    },
    questions_groups_index: {
      required: true,
      type: Number,
    },
  },

  data() {
    return {
      translations,
      ...translationKeys,
      optionConfig: advancedOptionToRenderConfig['none_of_the_above'],
      required: false,
      advancedOption: 'none_of_the_above',
      label: '',
    }
  },

  computed: {
    ...mapGetters(['questionGroups']),

    questionGroup() {
      return this.questionGroups[this.questions_groups_index]
    },

    nextQuestionsOnGroup() {
      return this.questionGroup.questions.filter(
        (_, index) => index > this.question_index,
      )
    },

    question() {
      return this.questionGroup.questions[this.question_index]
    },

    questionType() {
      return this.question.type
    },

    value() {
      return (
        this.questionGroups[this.questions_groups_index].questions[this.question_index]
          .advancedOptions[this.advancedOption] || null
      )
    },

    disabled() {
      const currentOptions = this.questionGroups[this.questions_groups_index].questions[
        this.question_index
      ].advancedOptions

      const isDisabled = this?.optionConfig.disableChecker?.(currentOptions)

      return isDisabled
    },

    renderOptions() {
      return renderOptions.filter(({ types }) =>
        types ? types.includes(this.questionType) : true,
      )
    },

    defaultLogicOptions() {
      return [
        { value: LOGIC_OPTIONS.NEXT, label: this.parseTranslation(this.NEXT) },
        { value: LOGIC_OPTIONS.SCREENED, label: this.parseTranslation(this.SCREENED) },
        { value: LOGIC_OPTIONS.FINISH, label: this.parseTranslation(this.FINISH) },
      ]
    },

    logicOptions() {
      const nextQuestions = this.nextQuestionsOnGroup.map(({ code, qid }) => ({
        value: qid,
        label: code,
      }))

      return [...this.defaultLogicOptions, ...nextQuestions]
    },
  },

  watch: {
    // future implementations will refactor this logic to
    // add in a callback to each adv_opt config that runs on each
    // type change up on our scheme component and adjusts/removes the value
    // accodingly - running side effects like this is not recommended
    questionType() {
      if (!this.value) return

      const { renderOn } = this.value

      const stillRelevant = this.renderOptions.find(({ value }) => value === renderOn)

      if (stillRelevant) return

      this.updateValue({
        ...this.value,
        renderOn: 'question_wide',
      })
    },

    nextQuestionsOnGroup() {
      if (!this.value) return

      const { logic } = this.value

      const defaultLogic = this.defaultLogicOptions.find(({ value }) => value === logic)

      if (defaultLogic) return

      const questionId = this.logicOptions.find(({ value }) => value === logic)

      if (questionId) return

      this.updateValue({
        ...this.value,
        logic: getFirstItem(this.defaultLogicOptions).value,
      })
    },

    language() {
      this.label = this.optionConfig.displayText[this.language]
    },
  },

  methods: {
    updateValue(newValue) {
      this.$store.dispatch('changeAdvanced', {
        questionGroupIndex: this.questions_groups_index,
        questionIndex: this.question_index,
        advancedKey: this.advancedOption,
        value: newValue,
      })
    },

    handleOptionToggle({ target: { checked } }) {
      this.updateValue(
        checked
          ? { code: '', label: '', logic: LOGIC_OPTIONS.NEXT, renderOn: 'question_wide' }
          : null,
      )
    },

    handleConfigChange({ target: { value, name } }) {
      const updatedValue = { ...this.value, [name]: value }

      this.updateValue(updatedValue)
    },
  },

  created() {
    this.label = this.optionConfig.displayText[this.language]

    this.optionConfig = advancedOptionToRenderConfig[this.advancedOption]
    this.required = this.optionConfig.required
  },
}
</script>

<style scoped lang="scss">
.checkbox-wrapper,
.input-group {
  display: flex;
  align-items: center;

  label {
    margin-right: rem(12);
  }

  input {
    padding: 5px 8px;
    border-radius: 4px;
    border: 1px solid $gray-6;
    outline: none;
    color: #292e33;
  }
}

.option-form {
  padding-left: 20px;
  padding-top: 12px;
}

.input-group {
  margin-top: rem(12);
}

select {
  @include advancedOptionSelect;
}
</style>
